import { Injectable } from '@angular/core';
import { generalHelpers, LanguageInterface } from '@mzima-client/sdk';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import LangJSON from '../../../assets/locales/languages.json';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private languages = new BehaviorSubject<LanguageInterface[]>(this.getLanguages());
  public languages$ = this.languages.asObservable();

  private languageKey = `${generalHelpers.CONST.LOCAL_STORAGE_PREFIX}language`;

  private selectedLanguage = new BehaviorSubject<string>('');
  public selectedLanguage$ = this.selectedLanguage.asObservable();
  private isRTL = new BehaviorSubject<boolean>(false);
  public isRTL$ = this.isRTL.asObservable();

  constructor(private translate: TranslateService) {
    this.translate.setDefaultLang('en');

    const storedLang = this.initialLanguage;
    const isStoredLangValid =
      storedLang !== 'null' &&
      storedLang !== null &&
      this.languages.value.find((language) => language.code === storedLang);

    if (isStoredLangValid) {
      this.setLanguage(storedLang!);
    } else {
      // Fallback: browser language → English
      // Note: Don't persist fallback to localStorage - this preserves cross-app preferences
      // (e.g., user may have a language saved from another app that this app doesn't support)
      const browserLang = this.detectBrowserLanguage();
      this.setLanguage(browserLang || 'en');
    }
  }

  get initialLanguage(): string | null {
    return localStorage.getItem(this.languageKey);
  }

  /**
   * Detects the user's preferred language from browser settings.
   * Checks navigator.languages array (ordered by preference) and returns
   * the first supported language code, or null if none match.
   */
  private detectBrowserLanguage(): string | null {
    const browserLanguages = navigator.languages || [navigator.language];

    for (const browserLang of browserLanguages) {
      // Extract base language code (e.g., 'en-US' → 'en')
      const langCode = browserLang.split('-')[0].toLowerCase();

      // Check if we support this language
      if (this.languages.value.find((lang) => lang.code === langCode)) {
        return langCode;
      }
    }

    return null;
  }

  public getLanguages(): LanguageInterface[] {
    return LangJSON.languages;
  }

  public getLanguageByCode(code: string): LanguageInterface | undefined {
    return this.languages.value.find((language) => language.code === code);
  }

  public setSelectedLanguage(code: string): void {
    this.setLanguage(code);
    localStorage.setItem(this.languageKey, code);
  }

  private changeDirection(lang: string) {
    const language = this.getLanguageByCode(lang);
    if (language) {
      this.isRTL.next(language.rtl || false);
    }
  }

  private setLanguage(lang: string) {
    this.translate.use(lang);
    this.selectedLanguage.next(lang);
    this.changeDirection(lang);
  }
}
